import { create } from 'zustand';
import { getFavoriteProperties as defaultGetFavoriteProperties } from '../utils/favourites';
import type { FavoriteProperty } from '../utils/favourites';
import { isNeverPublicStatus } from '../utils/properties';
import useTokenStore from './store';

interface FavoritesStore {
  favorites: FavoriteProperty[];
  favoriteIds: string[];
  isLoading: boolean;
  error: string | null;
  fetchFavorites: () => Promise<void>;
  removeFavorite: (propertyId: string) => void;
}

interface FavoritesStoreDeps {
  getFavoriteProperties?: typeof defaultGetFavoriteProperties;
}

type SetState = (partial: Partial<FavoritesStore> | ((state: FavoritesStore) => Partial<FavoritesStore>)) => void;

// State builder with an injectable fetcher so tests can rebuild the shared
// store with a fake instead of mocking modules.
export const buildFavoritesState = (set: SetState, deps: FavoritesStoreDeps = {}): FavoritesStore => {
  const getFavoriteProperties = deps.getFavoriteProperties ?? defaultGetFavoriteProperties;
  return {
    favorites: [],
    favoriteIds: [],
    isLoading: false,
    error: null,
    fetchFavorites: async () => {
      const token = useTokenStore.getState().token;
      if (!token) return;

      set({ isLoading: true, error: null });
      try {
        const response = await getFavoriteProperties(token);
        if (response.status === 200) {
          set({
            // The API omits never-public listings; filter again here as a backstop
            // for deploys that predate that. favoriteIds stay as served so a relist
            // restores the heart.
            favorites: response.results.filter((property) => !isNeverPublicStatus(property.publishStatus)),
            favoriteIds: response.favoriteIds,
            isLoading: false
          });
        } else {
          set({ error: response.message, isLoading: false });
        }
      } catch (error) {
        set({ error: 'Failed to fetch favorites', isLoading: false });
      }
    },
    removeFavorite: (propertyId: string) => {
      set((state) => ({
        favorites: state.favorites.filter(property => property._id !== propertyId),
        favoriteIds: state.favoriteIds.filter(id => id !== propertyId)
      }));
    }
  };
};

export const useFavoritesStore = create<FavoritesStore>((set) => buildFavoritesState(set));
